// ---Dependencies
import React from 'react';
// ---UI Dependencies
import { Brand } from '@dtci/spellbook/NavBar';
import LottiePlayer from '@dtci/spellbook/LottiePlayer';
import dneLogo from '../../../shared/assets/img/logo.svg';
import myIDLogo from '../../../shared/assets/img/myid.png';
import styles from './login.module.scss';
// ---Redux
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/auth';
// ---Configs
import { ENDPOINTS_KEYS } from '../../config/constants';
import { Header } from '../../components/Header';
import {
    isDealVersionFileDownloadUrl,
    normalizePostLoginRedirect,
} from '../../utils/dealVersionDownloadRedirect.js';

/**
 * Login Component
 *
 * This component serves as the login page for the application.
 * It includes a brand logo, a title, and a login button.
 * It also handles error states and loading indicators.
 *
 * @component
 * @returns {JSX.Element} The login page with branding and login functionality.
 **/
export const Login = () => {
    const dispatch = useDispatch();
    const error = useSelector((s) => s.auth.error);
    const { showSpinner } = useSelector((s) => s.spinner);
    const hasError = false;
    const [isStartingLogin, setIsStartingLogin] = React.useState(false);
    // Get postLoginRedirect from the URL and clean it up after displaying
    const [postLoginRedirect, setPostLoginRedirect] = React.useState("");
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const redirect = params.get("postLoginRedirect");
        const tokenFromUrl = params.get("token");
        const normalizedRedirect = redirect
            ? normalizePostLoginRedirect(redirect)
            : null;
        setPostLoginRedirect(normalizedRedirect || redirect || "");
        if (normalizedRedirect) {
            localStorage.setItem("postLoginRedirect", normalizedRedirect);
            params.delete("postLoginRedirect");
            const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
            window.history.replaceState({}, '', newUrl);
        } else if (!tokenFromUrl) {
            const stored = localStorage.getItem("postLoginRedirect");
            if (!isDealVersionFileDownloadUrl(stored)) {
                // No deep-link in URL: drop stale localStorage so post-login matches what the user sees
                // (SecuredApp now passes intent via ?postLoginRedirect= when bouncing from a protected route.)
                localStorage.removeItem("postLoginRedirect");
            }
        }
    }, []);

    /**
     * Clears any prior backend session (POST /users/logout when a token exists)
     * before navigating to MyID SSO.
     */
    async function handleMyIdLogin(event) {
        event.preventDefault();
        if (isStartingLogin) {
            return;
        }
        setIsStartingLogin(true);
        try {
            await dispatch(logout());
        } catch {
            // Thunk handles API errors; still proceed to SSO
        }
        window.location.assign(ENDPOINTS_KEYS.login);
    }

    const decodedPostLoginRedirect =
        normalizePostLoginRedirect(postLoginRedirect) ||
        normalizePostLoginRedirect(localStorage.getItem('postLoginRedirect')) ||
        postLoginRedirect;
    const showContinueToApiRedirect = isDealVersionFileDownloadUrl(decodedPostLoginRedirect);

    return (
        <>
        <Header />
        <div className={styles.root}>
            <div className={styles.leftPanel}>
                <div className={styles.bgImg} />
            </div>
            <div className={styles.rightPanel}>
                <div className={styles.content}>
                    <h1 className={styles.title} data-error={hasError}>
                        {hasError ? '403 Unauthorized' : 'Login to Datastage UI'}
                    </h1>
                    {showContinueToApiRedirect && (
                        <div style={{ marginBottom: 8, color: '#666', wordBreak: 'break-all', fontSize: 14 }}>
                            To continue to <span style={{ fontWeight: 500 }}>{decodedPostLoginRedirect}</span>
                        </div>
                    )}
                    <div className={styles.divider} data-error={hasError}>
                        <hr />
                        <span>{hasError ? 'You are not authorized' : 'Continue with'}</span>
                        <hr />
                    </div>
                    {showSpinner || isStartingLogin ? (
                        <LottiePlayer
                            style={{
                                position: 'relative',
                                maxWidth: '120px',
                                marginInline: 'auto',
                            }}
                        />
                    ) : (
                        <>
                            {hasError && <p className={styles.error}>{error}</p>}
                            {!hasError && (
                                <a
                                    href={ENDPOINTS_KEYS.login}
                                    className={styles.myID}
                                    onClick={handleMyIdLogin}
                                >
                                    Login with
                                    <img src={myIDLogo} alt="Disney MyID" />
                                </a>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
        </>
    );
};
